/**
 * Role-based session duration management.
 *
 * Strategy: Supabase JWT expiry is set to 1 hour. On each token refresh,
 * we compare the login timestamp (saved in localStorage) against the
 * role-specific max duration and force logout when exceeded.
 */

// ─── Duration constants (milliseconds) ──────────────────────────────────────

export const SESSION_DURATIONS = {
  /** Venue owner default: 24 hours */
  venue_owner: 24 * 60 * 60 * 1000,
  /** Venue owner with "Remember Me": 30 days */
  venue_owner_remember: 30 * 24 * 60 * 60 * 1000,
  /** Venue staff: 8 hours */
  venue_staff: 8 * 60 * 60 * 1000,
  /** Mall operator: 24 hours (same as venue owner) */
  mall_operator: 24 * 60 * 60 * 1000,
  /** Consumer: 30 days */
  consumer: 30 * 24 * 60 * 60 * 1000,
  /** Admin: 24 hours */
  admin: 24 * 60 * 60 * 1000,
  /** Admin with "Remember Me": 30 days (must not exceed the auth cookie max-age) */
  admin_remember: 30 * 24 * 60 * 60 * 1000,
} as const;

// ─── localStorage key suffixes ──────────────────────────────────────────────

const LOGIN_AT_SUFFIX = "_login_at";
const REMEMBER_ME_SUFFIX = "_remember_me";

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Save login timestamp (and optional rememberMe flag) to localStorage. */
export function saveLoginTimestamp(prefix: string, rememberMe = false): void {
  try {
    localStorage.setItem(prefix + LOGIN_AT_SUFFIX, String(Date.now()));
    if (rememberMe) {
      localStorage.setItem(prefix + REMEMBER_ME_SUFFIX, "1");
    } else {
      localStorage.removeItem(prefix + REMEMBER_ME_SUFFIX);
    }
  } catch {
    // localStorage may be disabled
  }
}

/** Retrieve the login timestamp from localStorage. */
export function getLoginTimestamp(prefix: string): number | null {
  try {
    const raw = localStorage.getItem(prefix + LOGIN_AT_SUFFIX);
    return raw ? parseInt(raw, 10) : null;
  } catch {
    return null;
  }
}

/** Check whether "Remember Me" was set at login. */
export function isRememberMe(prefix: string): boolean {
  try {
    return localStorage.getItem(prefix + REMEMBER_ME_SUFFIX) === "1";
  } catch {
    return false;
  }
}

/** Clear login timestamp and rememberMe flag (call on logout). */
export function clearLoginTimestamp(prefix: string): void {
  try {
    localStorage.removeItem(prefix + LOGIN_AT_SUFFIX);
    localStorage.removeItem(prefix + REMEMBER_ME_SUFFIX);
  } catch {
    // localStorage may be disabled
  }
}

/**
 * Check if the session has exceeded its max duration.
 * Returns false when no timestamp exists (legacy sessions / first login).
 */
export function isSessionExpired(prefix: string, maxDurationMs: number): boolean {
  const loginAt = getLoginTimestamp(prefix);
  if (loginAt === null) return false;
  return Date.now() - loginAt > maxDurationMs;
}

/** Resolve max session duration for a venue-app role. */
export function getVenueSessionDuration(
  role: "venue_owner" | "venue_staff" | "mall_operator" | "property_operator",
  rememberMe: boolean,
): number {
  if (role === "venue_staff") return SESSION_DURATIONS.venue_staff;
  if (role === "venue_owner" && rememberMe) return SESSION_DURATIONS.venue_owner_remember;
  return SESSION_DURATIONS.venue_owner;
}

/** Resolve max session duration for the admin app. */
export function getAdminSessionDuration(rememberMe: boolean): number {
  return rememberMe ? SESSION_DURATIONS.admin_remember : SESSION_DURATIONS.admin;
}
